import api from './api';

export interface VPNUser {
  id: string;
  email: string;
  displayName: string | null;
  tenantId: string;
  tenantName?: string;
  deviceGroupId: string | null;
  deviceGroupName?: string;
  deviceGroupIds: string[];
  deviceGroupNames: string[];
  status: number;
  maxDevices: number;
  lastConnection: string | null;
  connectionCount: number;
  keycloakSub: string | null;
  keycloakSynced: boolean;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  /**
   * The user's sign-in route (VSNP-1707): GET /vpn-users/:id for an admin, by
   * the API's own route() — the route after registration, and `domain`, the
   * tenant's verified domain the new apps find the provider by before sign-in.
   * Absent on an older API, or when the API could not work it out.
   */
  signInRoute?: VPNUserSignInRoute;
}

export interface VPNUserSignInRoute {
  method: 'browser' | 'password';
  providerId: string | null;
  providerAlias: string | null;
  providerType: 'azuread' | 'oidc' | null;
  providerName: string | null;
  /** A verified domain (A-label) matching the email, or null (in all mode: the new apps ask for a password first). */
  domain: string | null;
  reason: 'no_provider' | 'all_mode' | 'verified_domain' | 'no_verified_domain';
}

export interface VPNUserListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  tenantId?: string;
  status?: number;
  deviceGroupId?: string;
}

export interface VPNUserCreateData {
  email: string;
  displayName?: string;
  tenantId: string;
  deviceGroupId?: string;
  maxDevices?: number;
}

export interface VPNUserUpdateData {
  displayName?: string;
  deviceGroupId?: string | null;
  deviceGroupIds?: string[];
  status?: number;
  maxDevices?: number;
}

export type DemoAccountType = 'vpn_user' | 'tenant_admin';

export interface CreateDemoUserPayload {
  accountType: DemoAccountType;
  email: string;
  displayName: string;
  password: string;
  tenantId: string;
  roleId?: string;
  deviceGroupId?: string;
  maxDevices?: number;
}

export interface PaginatedVPNUserResponse {
  items: VPNUser[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const vpnUserService = {
  /**
   * List VPN users
   */
  list: async (params?: VPNUserListParams) => {
    const response = await api.get('/vpn-users', { params });
    return response.data as PaginatedVPNUserResponse;
  },

  /**
   * Get VPN user by ID
   */
  getById: async (id: string) => {
    const response = await api.get(`/vpn-users/${id}`);
    return response.data as VPNUser;
  },

  /**
   * Create VPN user
   */
  create: async (data: VPNUserCreateData) => {
    const response = await api.post('/vpn-users', data);
    return response.data as VPNUser;
  },

  /**
   * Update VPN user
   */
  update: async (id: string, data: VPNUserUpdateData) => {
    const response = await api.put(`/vpn-users/${id}`, data);
    return response.data as VPNUser;
  },

  /**
   * Delete VPN user
   */
  delete: async (id: string) => {
    await api.delete(`/vpn-users/${id}`);
  },

  /**
   * Move ALL of a user's devices to a different device group.
   * Forces re-registration of the affected devices.
   */
  moveDeviceGroup: async (id: string, deviceGroupId: string) => {
    const response = await api.put(`/vpn-users/${id}/device-group`, { deviceGroupId });
    return response.data as { movedDevices: number; deviceGroupName: string };
  },

  /**
   * Create a fully-activated demo account (SuperAdmin only).
   * No invite/email — password is set directly.
   */
  createDemoUser: async (payload: CreateDemoUserPayload) => {
    const response = await api.post('/admin/demo-users', payload);
    return response.data as { id: string; accountType: DemoAccountType; email: string; password: string };
  },

  /**
   * Enable VPN user
   */
  enable: async (id: string) => {
    const response = await api.post(`/vpn-users/${id}/enable`);
    return response.data as VPNUser;
  },

  /**
   * Disable VPN user
   */
  disable: async (id: string) => {
    const response = await api.post(`/vpn-users/${id}/disable`);
    return response.data as VPNUser;
  },

  /**
   * Provision multiple VPN users
   */
  provision: async (data: {
    emails: string[];
    tenantId: string;
    emailTemplateId?: string;
    expiresInDays?: number;
    deviceGroupIds: string[];
  }) => {
    const response = await api.post('/vpn-users/provision', data);
    return response.data as {
      provisioned: number;
      failed: number;
      results: Array<{
        email: string;
        status: 'invited' | 'already_exists' | 'invalid_email' | 'error';
        invitationId?: string;
        error?: string;
      }>;
    };
  },
};

export default vpnUserService;
